import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
  Text,
  Heading,
  Link,
  Image,
  ModalHeader,
} from '@everlywell/ui-kit';
import AddToCartButton from 'components/AddToCartButton';
import PriceV2 from 'components/PricingV2/PriceV2';
import { PricingOverride } from 'components/ProductCardV2/ProductCardV2.parser';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useRef } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { findActivePromotionForProduct } from 'utils/helpers/activePromoHelpers';
import { useProductBySlug, useProductVariants } from 'utils/hooks';
import useAdvertisedPromos from 'utils/hooks/useAdvertisedPromos';

import {
  ComparisonMarker,
  ListOfComparisonItems,
  MarkerCategory,
} from './components/ListOfComparisonItems/ListOfComparisonItems';

export type ProductComparisonModalProps = {
  data: Queries.pdpSectionFragment;
  productData: Queries.pdpProductFragment;
};

const formatPrice = (price: number | string) => {
  if (!price) {
    return {
      dollarAmount: '',
      centsAmount: '',
    };
  }

  // Convert price to string and split on decimal point
  const [dollars, cents = ''] = price.toString().split('.');

  // Pad cents with zeros if needed, or use '00' if no cents
  const formattedCents = cents.padEnd(2, '0').slice(0, 2);

  return {
    dollarAmount: dollars,
    centsAmount: formattedCents,
  };
};

const mergeItems = (
  listA: MarkerCategory[],
  listB: MarkerCategory[],
): MarkerCategory[] => {
  const itemMap = new Map<string, Set<string>>();

  // Create a map of items from listA
  listA?.forEach((category) => {
    itemMap?.set(
      category?.title,
      new Set(category?.markers?.map((item) => item?.name)),
    );
  });

  // Merge items from listB into listA
  listB?.forEach((categoryB) => {
    const categoryA = listA?.find(
      (category) => category?.title === categoryB?.title,
    );
    if (categoryA) {
      categoryB?.markers?.forEach((itemB) => {
        if (!itemMap?.get(categoryB.title)?.has(itemB.name)) {
          categoryA?.markers?.push({ name: itemB?.name, isExcluded: true });
        }
      });
    }
  });

  // Sort markers by name
  listA.forEach((category) => {
    category.markers.sort((a, b) =>
      a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()),
    );
  });

  return listA;
};

/**
 * Upsell Modal that compares a basic and premium version of a product.
 */
const ProductComparisonModal = (props: ProductComparisonModalProps) => {
  const { data, productData } = props;

  const { subsections, images, metadata, eyebrow } = data;
  const { spreeProduct } = productData;
  const entryIcon = images?.[0]?.iconFile?.file?.url;
  const entryIconAlt = images?.[0]?.desktopImageAltText;
  const entryTitle = metadata?.title;
  const productVariants = useProductVariants();
  const productsBySlug = useProductBySlug(productVariants);
  const { advertisedPromos } = useAdvertisedPromos();

  const { onOpen, onClose, isOpen } = useDisclosure();

  // Product Info A from Product Data

  const productAVariantId = Number(
    spreeProduct?.variants?.find((variant) => variant?.is_master)?.id,
  );

  // Type guard to check if subsection is of type Queries.ContentfulSection001
  function isContentfulSection001(
    subsection: any,
  ): subsection is Queries.ContentfulSection001 {
    return (
      subsection &&
      typeof subsection === 'object' &&
      'images' in subsection &&
      'metadata' in subsection &&
      'subsections' in subsection
    );
  }

  // Filter product info
  const extractSectionData = (section: Queries.ContentfulSection001) => {
    if (section?.body && section?.images && section?.metadata) {
      const { body, images, metadata } = section;
      return { body, images, metadata };
    }
    return null;
  };

  const productInfo = subsections
    ?.filter(isContentfulSection001) // Filter using the type guard
    ?.map((subsection) => {
      const { images, metadata, subsections } = subsection;
      const title = metadata?.title;
      const slug = metadata?.metadata_id;

      const whatsMeasured = subsections
        ?.map((section) =>
          extractSectionData(section as Queries.ContentfulSection001),
        )
        .filter(Boolean);

      return {
        title,
        images: images?.filter(Boolean) || null,
        whatsMeasured,
        slug,
      };
    });

  // Product Info A
  const productInfoA = productInfo && productInfo[0];
  const productA = spreeProduct?.slug
    ? productsBySlug[spreeProduct?.slug]
    : null;
  const promoProductA = findActivePromotionForProduct(
    spreeProduct?.productId ?? 0,
    advertisedPromos,
  );

  // Product Info B
  const productInfoB = productInfo && productInfo[1];
  const productBSlug = productInfoB?.slug;
  const productB = productBSlug ? productsBySlug[productBSlug] : null;
  const productBVariantId = Number(productB?.variantId);
  const promoProductB = findActivePromotionForProduct(
    productB?.productId ?? 0,
    advertisedPromos,
  );

  // Format items in each whatsMeasured section

  const formatItems = (
    whatsMeasured: Queries.ContentfulSection001[],
  ): MarkerCategory[] =>
    whatsMeasured?.map((item) => {
      const parsedBody = JSON.parse(item?.body?.raw || '');
      const textContent: string = parsedBody.content
        .map((node: { content: { value: string }[] }) =>
          node.content
            .map((childNode: { value: string }) => childNode.value)
            .join(''),
        )
        .join('\n');
      const markers: ComparisonMarker[] = textContent
        .split('\n')
        .map((str: string) => ({
          name: str.trim(),
        }))
        .map((marker) => {
          // sentece case
          const sentenceCaseMarker =
            marker.name.charAt(0).toUpperCase() +
            marker.name.slice(1).toLowerCase();
          return {
            name: sentenceCaseMarker,
          };
        });

      return {
        title: item?.metadata?.title ?? '',
        markers: markers ?? [],
        image: item?.images?.[0]?.iconFile?.file?.url ?? '',
      };
    });

  const listOfMarkersInProductA = productInfoA
    ? formatItems(productInfoA.whatsMeasured as Queries.ContentfulSection001[])
    : [];
  const listOfMarkersInProductB = productInfoB
    ? formatItems(productInfoB.whatsMeasured as Queries.ContentfulSection001[])
    : [];

  // Merge items from both products. If an item is not in listA, it is marked as excluded.
  const mergedItemsA = mergeItems(
    listOfMarkersInProductA,
    listOfMarkersInProductB,
  );

  // Ref for final focus after modal is closed
  const finalRef = useRef(null);

  const onCompareClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_LINK,
      data: {
        label: spreeProduct?.name
          ? `Click from ${spreeProduct.name} PDP`
          : 'Product Name Not Provided',
        component: 'ProductComparisonModal',
      },
    });
  };

  return (
    <>
      <Flex
        backgroundColor="viridian.wash"
        paddingX="8"
        paddingY="3"
        borderRadius="12px"
        alignItems="center"
        justifyContent="space-between"
        gap="4"
        flexDirection="row"
      >
        <Flex flexDirection="row" gap="1">
          <Image
            alt={entryIconAlt ?? ''}
            src={entryIcon ?? ''}
            width="20px"
            height="20px"
          />
          <Text>{entryTitle}</Text>
        </Flex>
        <Text
          as={Link}
          onClick={() => {
            onOpen();
            onCompareClick();
          }}
          color="viridian.base"
          ref={finalRef}
        >
          {eyebrow || 'Compare'}
        </Text>
      </Flex>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="3xl"
        finalFocusRef={finalRef}
      >
        <ModalOverlay />
        <ModalContent
          backgroundColor="tints.white"
          height={[null, null, '90vh']}
        >
          <ModalHeader>
            <Flex justifyContent="space-between" alignItems="center">
              <Text>Comparing</Text>
              <ModalCloseButton
                backgroundColor="tints.darkCream"
                width="32px"
                height="32px"
                position="static"
              />
            </Flex>
          </ModalHeader>
          <ModalBody paddingBottom="0">
            <Flex flexDirection="column" paddingTop="6" width="100%">
              {/* Container for product images and names (product a and product b) */}
              <Flex borderRadius="3" flexDirection="row">
                {/* Product A */}
                <Box
                  borderBottom="2px solid"
                  borderBottomColor="tints.white"
                  paddingX="4"
                  paddingTop="4"
                  paddingBottom="6"
                  width="50%"
                  backgroundColor="tints.darkCream"
                  borderTopRadius="xl"
                >
                  {productInfoA?.images &&
                    productInfoA.images[0]?.desktopImage?.gatsbyImageData && (
                      <Box width="72px" height="72px" paddingTop="4">
                        <GatsbyImage
                          image={
                            productInfoA.images[0].desktopImage.gatsbyImageData
                          }
                          alt={productInfoA.images[0].desktopImageAltText ?? ''}
                        />
                      </Box>
                    )}
                  <Heading size="sm">{productInfoA?.title}</Heading>
                </Box>

                {/* Product B */}
                <Box
                  borderBottom="2px solid"
                  borderBottomColor="tints.white"
                  paddingX="4"
                  paddingTop="4"
                  paddingBottom="6"
                  width="50%"
                  backgroundColor="tints.cream"
                  borderTopRadius="xl"
                >
                  {productInfoB?.images &&
                    productInfoB.images[0]?.desktopImage?.gatsbyImageData && (
                      <Box width="72px" height="72px" paddingTop="4">
                        <GatsbyImage
                          image={
                            productInfoB.images[0].desktopImage.gatsbyImageData
                          }
                          alt={productInfoB.images[0].desktopImageAltText ?? ''}
                        />
                      </Box>
                    )}
                  <Heading size="sm">{productInfoB?.title}</Heading>
                </Box>
              </Flex>

              {/*  List of markers product A x product B */}
              <Flex>
                <Box
                  backgroundColor="tints.darkCream"
                  width="50%"
                  paddingTop="6"
                  paddingX="4"
                >
                  <ListOfComparisonItems markerCategoryList={mergedItemsA} />
                </Box>
                <Box
                  backgroundColor="tints.cream"
                  width="50%"
                  paddingTop="6"
                  paddingX="4"
                >
                  <ListOfComparisonItems
                    markerCategoryList={listOfMarkersInProductB}
                  />
                </Box>
              </Flex>
            </Flex>

            {/* Footer */}
            <Flex
              flexDirection="row"
              width="100%"
              position="sticky"
              bottom="0"
              zIndex="1"
              borderTop="1px solid white"
            >
              <Box
                backgroundColor="tints.darkCream"
                padding="6"
                flex="1"
                borderBottomRadius="12px"
              >
                {promoProductA ? (
                  <PricingOverride
                    productId={productA?.productId}
                    outOfStock={productA?.outOfStock}
                    promotionable={productA?.promotionable}
                    price={productA?.price}
                    activePromo={promoProductA}
                  />
                ) : (
                  <PriceV2 {...formatPrice(productA?.price)} />
                )}

                {productInfoA?.title && productA?.slug ? (
                  <Box
                    marginTop="6"
                    sx={{
                      button: {
                        width: '100%',
                      },
                    }}
                  >
                    <AddToCartButton
                      product={{
                        variantId: productAVariantId,
                        displayName:
                          productInfoA?.title ?? 'Not provided (product A)',
                        slug: productA?.slug,
                        outOfStock: false,
                      }}
                      clickAnalyticsData={{
                        label: `${productInfoA?.title}`,
                        component: 'ProductComparisonModal',
                      }}
                    ></AddToCartButton>
                  </Box>
                ) : null}
              </Box>
              <Box
                backgroundColor="tints.cream"
                padding="6"
                flex="1"
                borderBottomRadius="12px"
              >
                {promoProductB ? (
                  <PricingOverride
                    productId={productB?.productId}
                    outOfStock={productB?.outOfStock}
                    promotionable={productB?.promotionable}
                    price={productB?.price}
                    activePromo={promoProductB}
                  />
                ) : (
                  <PriceV2 {...formatPrice(productB?.price)} />
                )}
                <Box
                  marginTop="6"
                  sx={{
                    button: {
                      width: '100%',
                    },
                  }}
                >
                  {productInfoB?.title && productB?.slug ? (
                    <AddToCartButton
                      product={{
                        variantId: productBVariantId,
                        displayName:
                          productInfoB?.title ?? 'Not provided (product B)',
                        slug: productB?.slug,
                        outOfStock: false,
                      }}
                      clickAnalyticsData={{
                        label: `${productInfoB?.title}`,
                        component: 'ProductComparisonModal',
                      }}
                      variant="secondary"
                    ></AddToCartButton>
                  ) : null}
                </Box>
              </Box>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ProductComparisonModal;
